import Phaser from "phaser";
import { Cat } from "../sprites/CatSprite";
import { PortalSprite } from "../sprites/PortalSprite";
import { CategoryData, CellBounds } from "../../config/types";
import {
  buildCoordinatePlaneBounds,
  CoordinatePlaneBounds,
} from "../../utils/grid";
import _ from "lodash";
import { AssetLocator } from "../sprites/AssetLocator";

export interface CatFactoryOptions {
  uiTopMargin?: number;
  /** Callback when a cat is clicked – receives its index */
  onCatClick?: (index: number) => void;
}

/**
 * Create Cat game objects for every category and return them in the same order.
 * This function encapsulates all sizing, spawning animations and interaction
 * wiring so the parent scene only has to call it and keep the returned array.
 */
export function createCats(
  scene: Phaser.Scene,
  categories: CategoryData[],
  cellBounds: CellBounds[],
  { uiTopMargin = 0, onCatClick }: CatFactoryOptions = {},
): Cat[] {
  const cats: Cat[] = [];
  let pendingFlyIns = 0;

  categories.forEach((category, index) => {
    const bounds = cellBounds[index];
    if (!bounds) return;

    // Determine cat number deterministically from category name so it stays stable
    const catNumber = getCatNumberForCategory(category.name);

    console.log(category);
    const catOptions = {
      catNumber,
      clothes: ["yellow_shirt"],
      hat: null,
    };
    if (category.necessary) {
      catOptions.hat = "crown";
    }

    // Final placement inside the grid cell (centered)
    const finalX = bounds.x + bounds.w / 2;
    const finalY = uiTopMargin + bounds.y + bounds.h / 2;

    // Spawn slightly above for a fly-in effect
    const spawnY = finalY - 150;

    const cat = new Cat(scene, finalX, spawnY, catOptions);

    // Scale cat to fit 60 % of the smallest cell dimension but max 300px
    const targetSize = Math.min(Math.min(bounds.w, bounds.h) * 0.6, 300);
    const catScale = targetSize / 200; // base sprite ~200px
    cat.setScale(catScale);

    // Set heart / stunned visuals
    cat.refreshFinancialStatus(category.budgeted, category.spent);

    // Fly-in animation only if not stunned
    const isStunned = category.spent > category.budgeted;
    if (!isStunned) {
      pendingFlyIns++;
      cat.animate("fly", false);
      scene.tweens.add({
        targets: cat,
        y: finalY,
        duration: 800,
        ease: "Cubic.easeOut",
        onComplete: () => {
          cat.animate("idle", true);
          cat.refreshFinancialStatus(category.budgeted, category.spent);
          pendingFlyIns--;
          if (pendingFlyIns === 0) {
            window.dispatchEvent(
              new CustomEvent("phaser-overlay-toggle", {
                detail: { visible: true },
              }),
            );
          }
        },
      });
    } else {
      cat.setY(finalY);
    }

    // Interaction – pass index back to scene
    cat.setInteractive();
    cat.on("pointerdown", () => onCatClick?.(index));

    scene.add.existing(cat);
    cats.push(cat);
  });

  // If no fly-ins were scheduled, reveal overlay immediately
  if (pendingFlyIns === 0) {
    window.dispatchEvent(
      new CustomEvent("phaser-overlay-toggle", { detail: { visible: true } }),
    );
  }

  return cats;
}

/**
 * Create Cat game objects for the coordinate plane layout.
 * Cats are positioned based on their surplus/deficit values and each gets their own row.
 */
export function createCatsInCoordinatePlane(
  scene: Phaser.Scene,
  categories: CategoryData[],
  viewWidth: number,
  viewHeight: number,
  {
    uiTopMargin = 0,
    onCatClick,
    levelPrecision = 100,
    fixedBounds,
  }: CatFactoryOptions & {
    levelPrecision?: number;
    fixedBounds?: { minSurplus: number; maxSurplus: number };
  } = {},
): Cat[] {
  const cats: Cat[] = [];
  let pendingFlyIns = 0;

  // Calculate coordinate plane bounds
  const coordinateBounds = buildCoordinatePlaneBounds(
    viewWidth,
    viewHeight - uiTopMargin,
    categories,
    levelPrecision,
    fixedBounds,
  );

  categories.forEach((category, index) => {
    const bounds = coordinateBounds[index];
    if (!bounds) return;

    // Determine cat number deterministically from category name so it stays stable
    const catNumber = getCatNumberForCategory(category.name);

    console.log(
      `Creating cat for ${category.name} at surplus=${bounds.surplus}, necessary=${bounds.necessary}`,
    );
    const clothesList = AssetLocator.getAvailableAccessories("clothes");
    const hatList = AssetLocator.getAvailableAccessories("hat").filter(
      (h) => h !== "crown",
    );
    const glassesList = AssetLocator.getAvailableAccessories("glasses");

    const randomSeed = category.name;

    const chosenClothes = maybePickAccessory(
      clothesList,
      randomSeed + "c",
      0.6,
    );
    const chosenGlasses = maybePickAccessory(
      glassesList,
      randomSeed + "g",
      0.5,
    );
    let chosenHat: string | null = null;

    if (category.necessary) {
      chosenHat = "crown";
    } else {
      chosenHat = maybePickAccessory(hatList, randomSeed + "h", 0.5) ?? null;
    }

    const catOptions: any = {
      catNumber,
      clothes: chosenClothes ? [chosenClothes] : [],
      hat: chosenHat,
      glasses: chosenGlasses,
    };

    // Final placement in coordinate plane (centered in cell)
    const finalX = bounds.x + bounds.w / 2;
    const finalY = uiTopMargin + bounds.y + bounds.h / 2;

    // Debug logging for cat positioning
    console.log(`Cat ${category.name} positioning:`, {
      bounds: { x: bounds.x, y: bounds.y, w: bounds.w, h: bounds.h },
      finalX,
      finalY,
      uiTopMargin,
      catScale: Math.min(Math.min(bounds.w, bounds.h) * 0.6, 300) / 200,
    });

    // Spawn slightly above for a fly-in effect
    const spawnY = finalY - 150;

    const cat = new Cat(scene, finalX, spawnY, catOptions);

    // Scale cat to fit the cell but max 300px
    const targetSize = Math.min(Math.min(bounds.w, bounds.h) * 0.6, 300);
    const catScale = targetSize / 200; // base sprite ~200px
    cat.setScale(catScale);

    // Set heart / stunned visuals
    cat.refreshFinancialStatus(category.budgeted, category.spent);

    // Fly-in animation only if not stunned
    const isStunned = category.spent > category.budgeted;
    if (!isStunned) {
      pendingFlyIns++;
      cat.animate("fly", false);
      scene.tweens.add({
        targets: cat,
        y: finalY,
        duration: 800,
        ease: "Cubic.easeOut",
        onUpdate: () => {
          // Live sync for overlay while flying in
          window.dispatchEvent(
            new CustomEvent("phaser-cat-position-updated", {
              detail: { index, x: cat.x, y: cat.y },
            }),
          );
        },
        onComplete: () => {
          cat.animate("idle", true);
          cat.refreshFinancialStatus(category.budgeted, category.spent);
          // Final sync
          window.dispatchEvent(
            new CustomEvent("phaser-cat-position-updated", {
              detail: { index, x: cat.x, y: cat.y },
            }),
          );
          pendingFlyIns--;
          if (pendingFlyIns === 0) {
            window.dispatchEvent(
              new CustomEvent("phaser-overlay-toggle", {
                detail: { visible: true },
              }),
            );
          }
        },
      });
    } else {
      cat.setY(finalY);
      // Directly sync position for stunned cats (no tween)
      window.dispatchEvent(
        new CustomEvent("phaser-cat-position-updated", {
          detail: { index, x: cat.x, y: cat.y },
        }),
      );
    }

    // Interaction – pass index back to scene
    cat.setInteractive();
    cat.on("pointerdown", () => onCatClick?.(index));

    scene.add.existing(cat);
    cats.push(cat);

    // Initial sync so React knows about cat immediately (spawn position)
    window.dispatchEvent(
      new CustomEvent("phaser-cat-position-updated", {
        detail: { index, x: cat.x, y: cat.y },
      }),
    );
  });

  // If no fly-ins were scheduled, reveal overlay immediately
  if (pendingFlyIns === 0) {
    window.dispatchEvent(
      new CustomEvent("phaser-overlay-toggle", { detail: { visible: true } }),
    );
  }

  return cats;
}

/** Move existing cats to updated cell bounds and rescale. */
export function repositionCats(
  cats: Cat[],
  cellBounds: CellBounds[],
  uiTopMargin: number = 0,
): void {
  cats.forEach((cat, index) => {
    const bounds = cellBounds[index];
    if (!bounds) return;
    const targetSize = Math.min(Math.min(bounds.w, bounds.h) * 0.6, 300);
    const catScale = targetSize / 200;
    cat.setPosition(
      bounds.x + bounds.w / 2,
      uiTopMargin + bounds.y + bounds.h / 2,
    );
    cat.setScale(catScale);
  });
}

/** Move existing cats to updated coordinate plane positions and rescale. */
export function repositionCatsInCoordinatePlane(
  cats: Cat[],
  categories: CategoryData[],
  viewWidth: number,
  viewHeight: number,
  uiTopMargin: number = 0,
  levelPrecision: number = 100,
  fixedBounds?: { minSurplus: number; maxSurplus: number },
): void {
  console.log("repositionCatsInCoordinatePlane called with:", {
    catsCount: cats.length,
    categories: categories.map((c) => ({
      name: c.name,
      surplus: c.budgeted - c.spent,
    })),
    viewWidth,
    viewHeight,
    uiTopMargin,
    levelPrecision,
    fixedBounds,
  });

  // Helper to manage the central portal sprite (singleton per scene)
  const sceneAny = (cats[0] ? (cats[0].scene as any) : undefined) as
    | (Phaser.Scene & {
        portalSprite?: PortalSprite;
        portalCounter?: number;
      })
    | undefined;

  if (sceneAny && sceneAny.portalCounter === undefined) {
    sceneAny.portalCounter = 0;
  }

  cats.forEach((cat, index) => {
    // Constant walk speed for balanced cats heading to center
    const WALK_SPEED_PX_PER_SEC = 150;

    const category = categories[index];
    if (!category) return;

    const surplus = category.budgeted - category.spent;
    const isBalanced = surplus === 0;

    // Only balanced cats can move; others stay put
    if (!isBalanced) return;

    const centerX = viewWidth / 2;
    const centerY = uiTopMargin + (viewHeight - uiTopMargin) / 2;

    const distance = Phaser.Math.Distance.Between(
      cat.x,
      cat.y,
      centerX,
      centerY,
    );

    // If already there or currently walking, do nothing
    if (distance <= 5 || (cat as any).currentAnimation === "walk") return;

    console.log(`Balanced cat ${index} (${category.name}) walking to center.`);

    // Increase portal counter and open portal if first cat
    if (sceneAny) {
      sceneAny.portalCounter = (sceneAny.portalCounter || 0) + 1;

      if (!sceneAny.portalSprite) {
        // Safety check: ensure cat scene exists before creating portal
        if (!cat || !cat.scene) {
          console.warn("Cannot create portal: cat or scene is undefined");
          return;
        }

        sceneAny.portalSprite = new PortalSprite(cat.scene, centerX, centerY);
        sceneAny.portalSprite.open();
        window.dispatchEvent(new CustomEvent("phaser-portal-open"));
        console.log("Portal opened at center:", centerX, centerY);
      }
    }

    const duration = Math.max((distance / WALK_SPEED_PX_PER_SEC) * 1000, 600);

    // Safety check: ensure cat and scene still exist
    if (!cat || !cat.scene) {
      console.warn("Cat or scene no longer exists, skipping animation");
      return;
    }

    // Start with jump animation
    cat.animate("jump", false);

    // After jump completes, start walking
    cat.scene.time.delayedCall(800, () => {
      // Safety check again after delay
      if (!cat || !cat.scene) {
        console.warn("Cat or scene no longer exists after jump delay");
        return;
      }

      cat.setFacingLeft(centerX < cat.x);
      cat.animate("walk", true);

      cat.scene.tweens.add({
        targets: cat,
        x: centerX,
        y: centerY,
        duration: duration,
        ease: "Linear",
        onUpdate: () => {
          window.dispatchEvent(
            new CustomEvent("phaser-cat-position-updated", {
              detail: { index, x: cat.x, y: cat.y },
            }),
          );
        },
        onComplete: () => {
          // Safety check before completing
          if (!cat || !cat.scene) {
            console.warn("Cat or scene no longer exists in onComplete");
            return;
          }

          // Cat disappears into the portal
          cat.setVisible(false);
          // Notify React overlay to hide this cat box
          window.dispatchEvent(
            new CustomEvent("phaser-cat-removed", { detail: { index } }),
          );
          cat.disableInteractive();
          // Optionally destroy after some time to free memory
          cat.scene.time.delayedCall(2000, () => cat.destroy());

          window.dispatchEvent(
            new CustomEvent("phaser-cat-position-updated", {
              detail: { index, x: cat.x, y: cat.y },
            }),
          );

          if (sceneAny) {
            sceneAny.portalCounter = Math.max(
              (sceneAny.portalCounter || 1) - 1,
              0,
            );

            if (sceneAny.portalCounter === 0 && sceneAny.portalSprite) {
              // After short delay, close the portal
              sceneAny.time.delayedCall(500, () => {
                sceneAny.portalSprite?.close(() => {
                  window.dispatchEvent(new CustomEvent("phaser-portal-closed"));
                  sceneAny.portalSprite = undefined;
                });
              });
            }
          }
        },
      });
    });
  });
}

/** Destroy all cat game objects safely. */
export function destroyCats(cats: Cat[]): void {
  cats.forEach((cat) => cat.destroy());
  cats.length = 0;
}

/* ───────── helpers ────────────────────────────────────────────── */
// Lifted from BudgetSceneReact to keep deterministic mapping stable
const CATEGORY_NAMES: string[] = [
  "Rent",
  "Home Insurance",
  "Utilities",
  "Internet & Phone",
  "Groceries",
  "Restaurants",
  "Coffee Shops",
  "Car Payment",
  "Car Insurance",
  "Gas",
  "Transportation",
  "Health",
  "Loans",
  "Clothing",
  "Shopping",
  "Personal Care",
  "Subscriptions",
  "Entertainment",
  "Gifts",
  "Travel",
  "Misc",
] as const;

function getCatNumberForCategory(categoryName: string): number {
  const index = CATEGORY_NAMES.indexOf(categoryName);
  if (index === -1) {
    const hash = categoryName
      .split("")
      .reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) & a, 0);
    return Math.abs(hash % 15) + 1;
  }
  return (index % 15) + 1;
}

function pickDeterministic<T>(list: T[], seed: string): T {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const idx = Math.abs(hash) % list.length;
  return list[idx];
}

function deterministicRandom(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  // Convert to 0..1 float
  return Math.abs(hash) / 0x7fffffff;
}

function maybePickAccessory<T>(
  list: T[],
  seed: string,
  probability: number,
): T | undefined {
  if (deterministicRandom(seed) < probability) {
    return pickDeterministic(list, seed);
  }
  return undefined;
}
